import { useState, useEffect } from "react";
import { Shield, CheckCircle, Clock, Lock, Loader2, Trophy, Users, Zap, Plus, Pencil, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { getSportEmoji, isFixtureLocked, getFixtureDates, formatDateShort, getFixturesByDate } from "@/lib/fixtures";
import { apiGetResults, apiSetResult, apiGetFixtures, apiCreateFixture, apiUpdateFixture, apiDeleteFixture } from "@/lib/storage";
import { UCL_LOGO, KCL_LOGO } from "@/components/Layout";
import { toast } from "sonner";

const BLANK_FIXTURE = { sport: "", teamA: "UCL", teamB: "KCL", date: "", time: "" };

export default function Admin() {
  const [results, setResults] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settingId, setSettingId] = useState(null);
  const [activeDate, setActiveDate] = useState("all");
  const [editingId, setEditingId] = useState(null); // fixture id being edited, or "new"
  const [form, setForm] = useState(BLANK_FIXTURE);
  const [saving, setSaving] = useState(false);
  const dates = getFixtureDates(fixtures);

  const refresh = async () => {
    setLoading(true);
    const [resultsData, fixturesData] = await Promise.all([apiGetResults(), apiGetFixtures()]);
    setResults(resultsData);
    setFixtures(fixturesData);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const startCreate = () => { setForm(BLANK_FIXTURE); setEditingId("new"); };
  const startEdit = (fixture) => { setForm({ sport: fixture.sport, teamA: fixture.teamA, teamB: fixture.teamB, date: fixture.date, time: fixture.time }); setEditingId(fixture.id); };
  const cancelEdit = () => { setEditingId(null); setForm(BLANK_FIXTURE); };

  const handleSaveFixture = async () => {
    setSaving(true);
    try {
      if (editingId === "new") {
        await apiCreateFixture(form);
        toast.success("Fixture added!");
      } else {
        await apiUpdateFixture(editingId, form);
        toast.success("Fixture updated!");
      }
      cancelEdit();
      await refresh();
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  const handleDeleteFixture = async (fixtureId) => {
    try {
      await apiDeleteFixture(fixtureId);
      toast.success("Fixture deleted!");
      await refresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const resultMap = {};
  results.forEach(r => { resultMap[r.fixture_id] = r; });

  const handleSetResult = async (fixtureId, winner) => {
    setSettingId(fixtureId);
    try {
      const data = await apiSetResult(fixtureId, winner);
      toast.success(`Result set: ${winner === "A" ? "UCL" : "KCL"} wins! ${data.scored_predictions} predictions scored.`);
      await refresh();
      window.dispatchEvent(new Event("predictions-updated"));
    } catch (err) {
      toast.error(err.message);
    }
    setSettingId(null);
  };

  const resolved = results.length;
  const remaining = fixtures.length - resolved;

  const filteredFixtures = activeDate === "all" ? fixtures : getFixturesByDate(fixtures, activeDate);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded bg-red-600/20 flex items-center justify-center">
          <Shield size={18} className="text-red-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Rajdhani', sans-serif" }}>ADMIN PANEL</h1>
          <p className="text-xs text-slate-500">Set fixture results and score predictions</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={14} className="text-green-400" />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest">Results Set</span>
          </div>
          <p className="text-2xl font-bold font-mono text-green-400" data-testid="admin-resolved">{resolved}</p>
        </div>
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock size={14} className="text-amber-400" />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest">Remaining</span>
          </div>
          <p className="text-2xl font-bold font-mono text-amber-400" data-testid="admin-remaining">{remaining}</p>
        </div>
        <div className="bg-[#111827] border border-white/5 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <Trophy size={14} className="text-red-400" />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest">Total Fixtures</span>
          </div>
          <p className="text-2xl font-bold font-mono text-red-400">{fixtures.length}</p>
        </div>
      </div>

      {/* Manage fixtures */}
      <div className="bg-[#111827] border border-white/5 rounded-sm p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Manage Fixtures</h2>
          {editingId === null && (
            <button
              onClick={startCreate}
              data-testid="admin-add-fixture-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors"
            >
              <Plus size={12} /> Add Fixture
            </button>
          )}
        </div>
        {editingId !== null && (
          <div className="bg-white/[0.03] border border-white/5 rounded-sm p-4 space-y-3" data-testid="admin-fixture-form">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Input placeholder="Sport (e.g. Hockey Men's)" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="bg-white/5 border-white/5 h-8 text-xs col-span-2 lg:col-span-1" data-testid="fixture-form-sport" />
              <Input placeholder="Team A" value={form.teamA} onChange={(e) => setForm({ ...form, teamA: e.target.value })} className="bg-white/5 border-white/5 h-8 text-xs" data-testid="fixture-form-teamA" />
              <Input placeholder="Team B" value={form.teamB} onChange={(e) => setForm({ ...form, teamB: e.target.value })} className="bg-white/5 border-white/5 h-8 text-xs" data-testid="fixture-form-teamB" />
              <Input placeholder="Date (e.g. 15 October 2026)" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-white/5 border-white/5 h-8 text-xs" data-testid="fixture-form-date" />
              <Input placeholder="Time (e.g. 18:00)" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="bg-white/5 border-white/5 h-8 text-xs" data-testid="fixture-form-time" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveFixture}
                disabled={saving}
                data-testid="admin-save-fixture-btn"
                className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : null}
                {editingId === "new" ? "Create Fixture" : "Save Changes"}
              </button>
              <button
                onClick={cancelEdit}
                data-testid="admin-cancel-fixture-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors"
              >
                <X size={12} /> Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Date filter */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-2 px-2">
        <button
          onClick={() => setActiveDate("all")}
          className={`px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-sm flex-shrink-0 transition-colors ${activeDate === "all" ? "bg-red-600 text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}
          data-testid="admin-date-all"
        >All</button>
        {dates.map(date => (
          <button
            key={date}
            onClick={() => setActiveDate(date)}
            className={`px-3 py-1.5 text-[10px] uppercase tracking-wider rounded-sm flex-shrink-0 transition-colors ${activeDate === date ? "bg-red-600 text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}
            data-testid={`admin-date-${date.replace(/\s/g, "-")}`}
          >{formatDateShort(date)}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={20} className="animate-spin text-slate-500" />
        </div>
      ) : (
        <div className="bg-[#111827] border border-white/5 rounded-sm overflow-hidden">
          <div className="red-gradient-line" />
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-0 px-4 py-2 text-[10px] text-slate-600 uppercase tracking-widest border-b border-white/5">
            <span>Fixture</span>
            <span className="w-24 text-center">Status</span>
            <span className="w-28 text-center">Set UCL Win</span>
            <span className="w-28 text-center">Set KCL Win</span>
            <span className="w-16 text-center">Manage</span>
          </div>
          {filteredFixtures.map(fixture => {
            const result = resultMap[fixture.id];
            const locked = isFixtureLocked(fixture);
            const isSetting = settingId === fixture.id;
            return (
              <div key={fixture.id} className={`grid grid-cols-[1fr_auto_auto_auto_auto] gap-0 items-center px-4 py-3 border-b border-white/[0.03] ${result ? "bg-green-500/[0.02]" : ""}`} data-testid={`admin-fixture-${fixture.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-base">{getSportEmoji(fixture.sport)}</span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-white truncate">{fixture.sport}</p>
                    <p className="text-[10px] text-slate-500 font-mono">{fixture.date} | {fixture.time}</p>
                  </div>
                </div>
                <div className="w-24 flex justify-center">
                  {result ? (
                    <Badge className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30 gap-1">
                      <CheckCircle size={8} /> {result.winner === "A" ? "UCL" : "KCL"}
                    </Badge>
                  ) : locked ? (
                    <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400 gap-1">
                      <Lock size={8} /> Awaiting
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-white/10 text-slate-500 gap-1">
                      <Clock size={8} /> Open
                    </Badge>
                  )}
                </div>
                <div className="w-28 flex justify-center">
                  <button
                    onClick={() => handleSetResult(fixture.id, "A")}
                    disabled={isSetting}
                    data-testid={`admin-set-ucl-${fixture.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${
                      result?.winner === "A"
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-white/5 hover:bg-red-500/10 text-slate-400 hover:text-white border border-white/5 hover:border-red-500/30"
                    }`}
                  >
                    {isSetting ? <Loader2 size={10} className="animate-spin" /> : <img src={UCL_LOGO} alt="" className="w-4 h-4 object-contain" />}
                    UCL
                  </button>
                </div>
                <div className="w-28 flex justify-center">
                  <button
                    onClick={() => handleSetResult(fixture.id, "B")}
                    disabled={isSetting}
                    data-testid={`admin-set-kcl-${fixture.id}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-bold transition-colors ${
                      result?.winner === "B"
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-white/5 hover:bg-red-500/10 text-slate-400 hover:text-white border border-white/5 hover:border-red-500/30"
                    }`}
                  >
                    {isSetting ? <Loader2 size={10} className="animate-spin" /> : <img src={KCL_LOGO} alt="" className="w-4 h-4 object-contain" />}
                    KCL
                  </button>
                </div>
                <div className="w-16 flex justify-center gap-1">
                  <button
                    onClick={() => startEdit(fixture)}
                    data-testid={`admin-edit-fixture-${fixture.id}`}
                    className="p-1.5 rounded hover:bg-white/10 text-slate-500 hover:text-white transition-colors"
                    title="Edit fixture"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => handleDeleteFixture(fixture.id)}
                    data-testid={`admin-delete-fixture-${fixture.id}`}
                    className="p-1.5 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
                    title="Delete fixture"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
